import { useState } from "react";
import { UserRoundPen } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { inputCls } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { DriverPhotoEditor } from "@/components/DriverPhoto";

const ProfileForm = ({ user, onDone }) => {
  const { refresh } = useAuth();
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "+675 " });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return setError("Please enter your full name.");
    if (!/^\+?[0-9 ]{7,18}$/.test(f.phone.trim())) return setError("Enter a valid mobile number, e.g. +675 7123 4567.");
    setBusy(true);
    setError("");
    try {
      await api.put("/auth/me/profile", { name: f.name.trim(), phone: f.phone.trim() });
      await refresh();
      toast.success("Profile updated");
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {user.role === "driver" && <DriverPhotoEditor />}
      <div className="space-y-1.5">
        <Label htmlFor="profile-name-input">Full name</Label>
        <input id="profile-name-input" data-testid="profile-name-input" required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="profile-phone-input">Mobile number</Label>
        <input id="profile-phone-input" data-testid="profile-phone-input" type="tel" inputMode="tel" required className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      </div>
      <p className="text-xs text-slate-500">Email: {user.email} (contact Comfort Taxi staff to change it)</p>
      {error && <p data-testid="profile-error" className="text-sm text-red-600">{error}</p>}
      <button data-testid="profile-save-button" disabled={busy} className="w-full rounded-full bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60">
        {busy ? "Saving…" : "Save changes"}
      </button>
    </form>
  );
};

export const EditProfileButton = ({ className, testId = "edit-profile-button", iconOnly = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!["customer", "driver"].includes(user?.role)) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button data-testid={testId} onClick={() => setOpen(true)} className={className} aria-label="Edit my profile">
        <UserRoundPen className={iconOnly ? "h-5 w-5" : "h-3.5 w-3.5"} />
        {!iconOnly && "Edit my profile"}
      </button>
      <DialogContent data-testid="edit-profile-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>My profile</DialogTitle>
          <DialogDescription>Your name and number are shared with your {user.role === "driver" ? "passengers" : "driver"} for each trip.</DialogDescription>
        </DialogHeader>
        {open && <ProfileForm user={user} onDone={() => setOpen(false)} />}
      </DialogContent>
    </Dialog>
  );
};
